import PropTypes from 'prop-types';
import { Typography, makeStyles, alpha } from '@material-ui/core';
import UserCount from '../../../../../../userCount';
import UserCountBadge from '../../../../../../userCountBadge';
import DotsMenu from '../../../../../../dotsMenu';
import TextBoxWithTooltip from '../../../../../../textBoxWithTooltip';
import { getMenuItems } from '../../../../config';
import { getUserStatusLabel } from '../../../../../../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    border: `1px dashed ${primary.bluish5}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    width: 300,
    height: 194,
    transition: 'box-shadow .3s ease',
    '&:hover': {
      boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.12)}`,
    },
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: spacing(2),
  },
  actionsMenu: {
    flexShrink: 0,
  },
  description: {
    height: 14,
    marginTop: spacing(1),
    marginBottom: spacing(7),
  },
  daysCountLabel: {
    color: primary.bluish4,
  },
  userCount: {
    flexShrink: 0,
    backgroundColor: 'unset',
    cursor: 'default',
    opacity: 0.2,
  },
}));

const Extension = ({
  translations,
  extension,
  typeUsersCount,
  onEdit,
  onDelete,
  onGetUsers,
  onRemoveUsers,
  canAccessUserProfileCheck,
  onGoToProfilePage,
  onAddUsers,
}) => {
  const classes = useStyles();

  const { name, description, user_count, number_of_days } = extension;
  const hasExtensionUsers = user_count !== 0;
  const hasTypeUsers = typeUsersCount - user_count;

  const extensionMenuItems = getMenuItems(
    translations.menuItemLabels,
    extension,
    onEdit,
    onDelete
  );

  return (
    <div className={classes.root}>
      <div className={classes.info}>
        <TextBoxWithTooltip variant="subtitle1" text={name} />
        <div className={classes.actionsWrapper}>
          {hasExtensionUsers || hasTypeUsers ? (
            <UserCount
              actionButtonLabel={translations.remove}
              addButtonLabel={translations.add}
              userCount={user_count}
              shouldDisablePortal={false}
              getUsers={onGetUsers}
              onTooltipAction={onRemoveUsers}
              onTooltipAdd={onAddUsers(extension)}
              canAccessUserProfileCheck={canAccessUserProfileCheck}
              goToUserProfile={onGoToProfilePage}
              shouldHandleTypeLabel
              handleTypeLabel={user =>
                getUserStatusLabel(translations.deactivated, user)
              }
              hasAddButton
              hasActionButton
              tooltipButtonProps={{
                type: 'removeDarkRoundedOutlined',
                disabled: !hasExtensionUsers,
              }}
              addTooltipButtonProps={{
                disabled: !hasTypeUsers,
              }}
            />
          ) : (
            <UserCountBadge
              className={classes.userCount}
              userCount={user_count}
            />
          )}
          <div className={classes.actionsMenu}>
            <DotsMenu menuItems={extensionMenuItems} isVerticalMenu />
          </div>
        </div>
      </div>
      <TextBoxWithTooltip
        className={classes.description}
        variant="caption"
        text={description}
      />
      <div>
        <Typography className={classes.daysCountLabel} variant="caption">
          {translations.daysCountLabel}
        </Typography>
        <Typography variant="h3">
          {`+ ${number_of_days} ${
            number_of_days === 1 ? translations.day : translations.days
          }`}
        </Typography>
      </div>
    </div>
  );
};

Extension.propTypes = {
  translations: PropTypes.object.isRequired,
  extension: PropTypes.object.isRequired,
  typeUsersCount: PropTypes.number.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onGetUsers: PropTypes.func.isRequired,
  onRemoveUsers: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  onGoToProfilePage: PropTypes.func.isRequired,
  onAddUsers: PropTypes.func.isRequired,
};

export default Extension;
